/**
 * Custom hook for tracking network online/offline status
 * and triggering offline data sync
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import { setupNetworkListeners, getAllPendingMessages, clearPendingMessages, deletePendingMessage } from '../pwa/offlineStorage';
import { apiClient } from '../api';
import { useTranslation } from 'react-i18next';

export const useNetworkStatus = () => {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const offlineTimeoutRef = useRef(null);

  const syncPendingData = useCallback(async () => {
    if (isSyncing) return;

    setIsSyncing(true);
    try {
      const pendingMessages = await getAllPendingMessages();
      if (pendingMessages.length === 0) {
        setIsSyncing(false);
        return;
      }

      message.info(t('network.syncingMessages', { count: pendingMessages.length }));

      let successCount = 0;
      let failCount = 0;

      for (const pendingMessage of pendingMessages) {
        try {
          // Attempt to send the message to backend
          await apiClient.post(`/conversation/${pendingMessage.conversationId}/message`, {
            content: pendingMessage.content,
            timestamp: pendingMessage.createdAt,
          });

          // Remove from pending queue on success
          await deletePendingMessage(pendingMessage.tempId);
          successCount++;
        } catch (error) {
          console.error('Failed to sync message:', error);
          failCount++;
        }
      }

      if (successCount > 0) {
        message.success(t('network.syncSuccess', { count: successCount }));
      }
      if (failCount > 0) {
        message.warning(t('network.syncFailed', { count: failCount }));
      }
    } catch (error) {
      console.error('Error syncing pending data:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing]);

  const handleOnline = useCallback(() => {
    // Cancel pending offline transition if connection restored quickly
    if (offlineTimeoutRef.current) {
      clearTimeout(offlineTimeoutRef.current);
      offlineTimeoutRef.current = null;
    }
    setIsOnline(true);
    if (wasOffline) {
      message.success(t('network.connectionRestored'));
      // Trigger sync of any pending data
      syncPendingData();
    }
    setWasOffline(false);
  }, [wasOffline, syncPendingData]);

  const handleOffline = useCallback(() => {
    // Delay offline transition by 3 seconds to avoid brief network blips
    if (offlineTimeoutRef.current) return; // Already waiting
    offlineTimeoutRef.current = setTimeout(() => {
      offlineTimeoutRef.current = null;
      setIsOnline(false);
      setWasOffline(true);
      message.warning(t('network.offline'));
    }, 3000);
  }, []);

  useEffect(() => {
    const cleanup = setupNetworkListeners(handleOnline, handleOffline);
    return () => {
      cleanup();
      if (offlineTimeoutRef.current) {
        clearTimeout(offlineTimeoutRef.current);
      }
    };
  }, [handleOnline, handleOffline]);

  return { isOnline, wasOffline, isSyncing, syncPendingData };
};

export default useNetworkStatus;

